// FamMatch — pool model.
//
// Family-side UI:
//   1. Compose a request (need / district / language / notes).
//   2. Post it: a row in match_requests lands with status = 'open'.
//      The family's org-admin sees it in the org's Requests tab and
//      types a helper name + phone to assign it.
//   3. The org admin's assign call flips the request to 'assigned'
//      and notifies the family. The helper_name + helper_phone are
//      the only contact info exchanged — the family never sees a
//      separate helpers table.
//   4. The family sees the assigned request, can confirm
//      ('confirmed') or decline ('declined'). They can also withdraw
//      an 'open' request before the org admin touches it.
//
// The quota is consumed at step 2. `consume_match_quota` is
// server-side and authoritative (single-statement CTE — migration 029).
//
// Post-04918: no match_intakes insert (intake fields are inlined
// onto the match_requests row). No helpers join (the org admin types
// the helper contact on assignment).

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  CalendarClock,
  Check,
  HeartHandshake,
  Plus,
  Send,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { M3Card, M3CardHeader } from "@/components/M3Shared";
import { M3BottomSheet } from "@/components/M3BottomSheet";
import { registerFab } from "@/components/FabRegistry";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { useSupabaseQuery } from "@/lib/dbHooks";
import { supabase, isAuthConfigured } from "@/lib/supabase";
import { SessionBadge } from "@/pages/modules/fam-match/SessionBadge";
import {
  DISTRICTS,
  LANGUAGES,
  LITE_QUOTA,
  type RequestStatus,
} from "@/pages/modules/fam-match/types";

/* Re-exports for callers that import from this module. */
export { DISTRICTS, LANGUAGES, LITE_QUOTA } from "@/pages/modules/fam-match/types";
export type { RequestStatus } from "@/pages/modules/fam-match/types";
export { SessionBadge } from "@/pages/modules/fam-match/SessionBadge";
const DEFAULT_NOTES = "請喺備註講低聯絡時間、特別需要或照顧細節。";
const TOAST_DURATION_MS = 3_200;

export function FamMatchView() {
  const me = useCurrentUser();
  const cf = useCurrentFamily(me.user?.id ?? null);

  const [composeOpen, setComposeOpen] = useState(false);
  const [need, setNeed] = useState<string>("");
  const [district, setDistrict] = useState<string>("屯門");
  const [language, setLanguage] = useState<string>("不限");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    registerFab({
      label: "新增配對請求",
      icon: Plus,
      onClick: () => setComposeOpen((s) => !s),
    });
    return () => registerFab(null);
  }, []);

  // Read this family's match_requests directly. No join needed —
  // every field the family cares about (need / district / language /
  // notes / helper_name / helper_phone / status) lives on the row.
  const myRequests = useSupabaseQuery<Array<{
    id: string;
    status: string;
    created_at: string;
    need: string;
    district: string;
    language: string;
    accessibility_notes: string | null;
    helper_name: string | null;
    helper_phone: string | null;
  }>>(async () => {
    if (!isAuthConfigured() || !me.user?.id) return { data: [], error: null };
    return await supabase
      .from("match_requests")
      .select("id, status, created_at, need, district, language, accessibility_notes, helper_name, helper_phone")
      .eq("user_id", me.user.id)
      .order("created_at", { ascending: false });
  }, [me.user?.id]);

  // Quota used THIS month — server-side via get_match_quota_used(),
  // so the pill copy matches consume_match_quota's
  // `date_trunc('month', now())`.
  const quotaUsed = useSupabaseQuery<number>(async () => {
    if (!isAuthConfigured() || !me.user?.id) return { data: 0, error: null };
    const { data, error } = await (supabase.rpc as unknown as (
      name: string,
    ) => Promise<{ data: unknown; error: { message: string } | null }>)(
      "get_match_quota_used",
    );
    if (error || typeof data !== "number") return { data: 0, error };
    return { data, error: null };
  }, [me.user?.id]);

  const requestsThisMonth = quotaUsed.data ?? 0;
  const remaining = Math.max(0, LITE_QUOTA - requestsThisMonth);
  const overQuota = remaining === 0;

  const fireToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };

  const postRequest = async (): Promise<void> => {
    if (overQuota || !me.user?.id || !cf.family) {
      if (overQuota) fireToast(`本月配對額度已用完 (0 / ${LITE_QUOTA})`);
      return;
    }
    setSubmitting(true);
    const { error: qErr } = await supabase.rpc("consume_match_quota");
    if (qErr) {
      setSubmitting(false);
      fireToast(`本月配對額度已用完 (0 / ${LITE_QUOTA})`);
      quotaUsed.refresh();
      return;
    }
    // Single insert: the intake fields are now inlined on the
    // match_requests row (04918 collapsed match_intakes in). helper_*
    // stay NULL until the org admin assigns.
    const { error: rErr } = await supabase.from("match_requests").insert({
      user_id: me.user.id,
      family_id: cf.family.id,
      need,
      district: district === "不限" ? "屯門" : district,
      language: language === "不限" ? "粵語" : language,
      accessibility_notes: notes.trim() || null,
      status: "open",
    });
    setSubmitting(false);
    if (rErr) {
      // Quota already incremented; family must withdraw to refund.
      // The withdraw path is reachable from the open-requests list.
      fireToast("送出配對請求失敗：" + rErr.message);
      return;
    }
    setComposeOpen(false);
    setNotes("");
    fireToast("已送出配對請求 · 機構會喺 24 小時內指派幫手");
    quotaUsed.refresh();
    myRequests.refresh();
  };

  const withdraw = async (requestId: string): Promise<void> => {
    setBusyId(requestId);
    const { error } = await (supabase.rpc as unknown as (
      name: string,
      params: { p_request_id: string },
    ) => Promise<{ data: unknown; error: { message: string } | null }>)(
      "match_pool_withdraw",
      { p_request_id: requestId },
    );
    setBusyId(null);
    if (error) {
      fireToast("撤回失敗：" + error.message);
      return;
    }
    fireToast("已撤回配對請求 · 額度已退回");
    quotaUsed.refresh();
    myRequests.refresh();
  };

  const respond = async (
    requestId: string,
    next: "confirmed" | "declined",
  ): Promise<void> => {
    setBusyId(requestId);
    const { error } = await (supabase.rpc as unknown as (
      name: string,
      args: { p_request_id: string; p_next: "confirmed" | "declined" },
    ) => Promise<{ error: { message: string } | null }>)(
      "match_pool_respond",
      { p_request_id: requestId, p_next: next },
    );
    setBusyId(null);
    if (error) {
      fireToast("更新失敗：" + error.message);
      return;
    }
    fireToast(next === "confirmed" ? "已確認 · 等待服務" : "已婉拒");
    myRequests.refresh();
  };

  // Group requests into the three buckets the family cares about.
  const groups = useMemo(() => {
    const open: Array<NonNullable<typeof myRequests.data>[number]> = [];
    const action: Array<NonNullable<typeof myRequests.data>[number]> = [];
    const closed: Array<NonNullable<typeof myRequests.data>[number]> = [];
    for (const r of myRequests.data ?? []) {
      if (r.status === "open") {
        open.push(r);
      } else if (r.status === "assigned") {
        action.push(r);
      } else {
        closed.push(r);
      }
    }
    return { open, action, closed };
  }, [myRequests.data]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <motion.header
        className="px-1 pt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs text-muted-foreground sm:text-sm">
          FamMatch · 對的人，剛好在身邊
        </p>
        <h1 className="mt-1 font-serif text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          <span className="text-primary">提出請求</span>，由機構配對。
        </h1>
        <p className="mt-2 max-w-2xl text-pretty text-sm text-muted-foreground sm:text-base">
          寫低你嘅需要，機構嘅管理員會喺佢哋嘅幫手名單中揀選合適嘅一位，
          24 小時內會回覆你。地址同家庭成員名單從不外洩。
        </p>
      </motion.header>

      {/* Quota pill */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <span
          aria-label={overQuota
            ? `本月配對額度已用完 (0 / ${LITE_QUOTA})`
            : `本月仲有 ${remaining} / ${LITE_QUOTA} 次配對請求`}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
            overQuota
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : remaining === 1
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-border/60 bg-secondary/30 text-foreground/85",
          )}
        >
          <CalendarClock className="h-3 w-3" aria-hidden />
          {overQuota
            ? `本月配對額度已用完 (0 / ${LITE_QUOTA})`
            : `本月仲有 ${remaining} / ${LITE_QUOTA} 次配對請求`}
        </span>
        {overQuota ? (
          <span className="text-[11px] text-muted-foreground">
            升級至 Core 可享不限次數配對、專屬個案經理同 FamStatus 缺勤升級對接。
          </span>
        ) : null}
      </div>

      {/* In-flight request the family must act on */}
      {groups.action.length > 0 && (
        <M3Card>
          <M3CardHeader title="機構已指派 · 等你確認" />
          <Stagger as="ul" className="mt-3 space-y-2" gap={0.05}>
            {groups.action.map((req) => (
              <StaggerItem key={req.id} as="li">
                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">
                        {req.helper_name ?? "機構處理中"}
                        {req.helper_phone && (
                          <span className="text-[11px] text-muted-foreground">
                            {" · "}{req.helper_phone}
                          </span>
                        )}
                        {" · "}
                        {req.need ?? "—"}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {req.district ?? "—"} · {req.language ?? "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      disabled={busyId === req.id}
                      onClick={() => void respond(req.id, "confirmed")}
                    >
                      確認由呢位幫手服務
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === req.id}
                      onClick={() => void respond(req.id, "declined")}
                    >
                      婉拒
                    </Button>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </M3Card>
      )}

      {/* Open requests — family can withdraw any of them */}
      <M3Card>
        <M3CardHeader title="等待機構配對" trailing={`${groups.open.length}`} />
        {groups.open.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            暫未有送出中嘅請求。
          </p>
        ) : (
          <Stagger as="ul" className="mt-3 space-y-2" gap={0.05}>
            {groups.open.map((req) => (
              <StaggerItem key={req.id} as="li">
                <div className="flex items-center gap-3 rounded-2xl border border-border/40 bg-card p-3">
                  <span
                    aria-hidden
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary text-foreground/60"
                  >
                    <HeartHandshake className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      {req.need ?? "—"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {req.district ?? "—"} · {req.language ?? "—"} · 機構配對中
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busyId === req.id}
                    onClick={() => void withdraw(req.id)}
                  >
                    <X className="mr-1 h-3.5 w-3.5" aria-hidden />
                    撤回
                  </Button>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </M3Card>

      {/* Closed history */}
      <M3Card>
        <M3CardHeader title="過往配對" trailing={`${groups.closed.length}`} />
        {groups.closed.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            尚未有已確認、已完成或已婉拒嘅請求。
          </p>
        ) : (
          <Stagger as="ul" className="mt-3 space-y-2" gap={0.05}>
            {groups.closed.map((r) => (
              <StaggerItem key={r.id} as="li">
                <div className="flex items-center gap-3 rounded-2xl border border-border/40 bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      {r.helper_name ?? "—"}{" · "}
                      {r.need ?? "—"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.status === "completed"
                        ? "已完成"
                        : r.status === "declined"
                          ? "已婉拒"
                          : r.status === "confirmed"
                            ? "已確認"
                            : "處理中"}
                    </div>
                  </div>
                  <SessionBadge status={r.status as RequestStatus} />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </M3Card>

      {/* Privacy footer */}
      <p className="px-2 pt-1 text-center text-[11px] leading-relaxed text-muted-foreground">
        FamMatch 守則 · 你嘅地址、家庭成員名單、實時位置從不分享畀幫手。
        每次配對前需獨立同意 · 你可以隨時撤回送出中嘅請求、重新配對或封鎖任何幫手。
      </p>

      {/* Compose new request */}
      <AnimatePresence>
        {composeOpen ? (
          <M3BottomSheet
            onClose={() => setComposeOpen(false)}
            title="新增配對請求"
          >
            <div className="space-y-4">
              <div
                className={cn(
                  "flex items-start gap-3 rounded-2xl border p-3",
                  overQuota
                    ? "border-rose-200 bg-rose-50"
                    : "border-primary/30 bg-primary/5",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-primary-foreground",
                    overQuota ? "bg-rose-500" : "bg-primary",
                  )}
                >
                  <Send className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-semibold">
                    {overQuota
                      ? "本月配對額度已用完"
                      : `本月仲有 ${remaining} / ${LITE_QUOTA} 次配對請求`}
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    送出後，機構嘅管理員會喺佢哋嘅幫手名單中揀選合適嘅一位。
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-border/60 bg-secondary/30 p-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                  設定條件
                </div>
                <div className="mt-2 space-y-3">
                <div>
                  <label
                    htmlFor="match-create-need"
                    className="text-[11px] font-medium text-muted-foreground"
                  >
                    需要類型
                  </label>
                  <Textarea
                    id="match-create-need"
                    value={need}
                    onChange={(e) => setNeed(e.currentTarget.value)}
                    placeholder="一句話描述你需要嘅幫助。例如：夜晚陪瞓、覆診車資、跨代照顧情緒等。"
                    maxLength={80}
                    rows={2}
                    className="mt-1 w-full rounded-xl border-border/60 bg-background px-3 py-2 text-sm"
                  />
                  <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>最多 80 字 · 機構管理員只睇到你嘅描述。</span>
                    <span className="tabular-nums">{need.length} / 80</span>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <span className="text-[11px] font-medium text-muted-foreground">地區</span>
                      <Select value={district} onValueChange={setDistrict}>
                        <SelectTrigger className="mt-1 h-10 w-full rounded-xl border-border/60 bg-background px-3 text-sm focus:ring-primary/30">
                          <SelectValue placeholder="不限" />
                        </SelectTrigger>
                        <SelectContent>
                          {DISTRICTS.map((d) => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <span className="text-[11px] font-medium text-muted-foreground">語言</span>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="mt-1 h-10 w-full rounded-xl border-border/60 bg-background px-3 text-sm focus:ring-primary/30">
                          <SelectValue placeholder="不限" />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map((l) => (
                            <SelectItem key={l} value={l}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-medium text-muted-foreground">
                  備註（時間、聯絡方式、特別需要）
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  placeholder={DEFAULT_NOTES}
                  rows={4}
                  className="mt-1 w-full rounded-xl border-border/60 bg-background px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  地址同成員名單從不外洩 · 機構管理員只睇到呢段備註同你選擇嘅條件
                </p>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setComposeOpen(false)}
                  disabled={submitting}
                >
                  取消
                </Button>
                <Button
                  onClick={() => void postRequest()}
                  disabled={submitting || overQuota}
                >
                  {submitting ? "送出中…" : "送出配對請求"}
                </Button>
              </div>
            </div>
          </M3BottomSheet>
        ) : null}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast ? (
          <motion.div
            role="status"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="pointer-events-none fixed inset-x-0 bottom-24 z-40 mx-auto flex max-w-xs items-center gap-2 rounded-2xl bg-foreground px-4 py-3 text-sm text-background shadow-lg lg:bottom-6"
          >
            <Check className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1">{toast}</span>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default FamMatchView;
